import classNames from "classnames";
import {observer} from "mobx-react-lite";
import React, {RefObject, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useStores} from "../../../stores/useStores";
import {useScrollPosition} from "../../../utils/ui/useScrollPosition";
import Tag from "../../atoms/tag/tag";
import MultiSelectBookingOptions from "../../organisms/multiSelectBookingOptions/multiSelectBookingOptions";
import EventBookingsConfirmedTable from "../../organisms/eventBookingsConfirmedTable/eventBookingsConfirmedTable";
import EventBookingsWaitlistTable from "../../organisms/eventBookingsWaitlistTable/eventBookingsWaitlistTable";
import "./manageBookingsTemplate.sass";
import SearchBox, {SearchBoxType} from "../../atoms/input/searchBox";
import {TextObservable} from "../../../stores/observables/textObservable";
import EventBookingsSearchTable from "../../organisms/eventBookingsSearchTable/eventBookingsSearchTable";
import Banner from "../../atoms/banner/banner";
import {IconSource} from "../../../shared/enums/iconSource";
import {EventDetailsHelper} from "../../../shared/eventDetailsHelper";
import EventBookingsCancelledTable from "../../organisms/eventBookingsCancelledTable/eventBookingsCancelledTable";
import ButtonIcon from "../../atoms/button/buttonIcon";
import CsvDownloader from "react-csv-downloader";
import {format} from "date-fns";
import {EventPaidType} from "../../../shared/enums/eventPaidType";
import {residentCsvExporterConfigs} from "../../../shared/residentCsvExporterConfigs";

enum CurrentLocation {
  confirmed,
  waitlist,
  cancelled
}

const ManageBookingsTemplate: React.FC = () => {
  const {t} = useTranslation(["translation", "event"]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollElementRef = useRef<HTMLDivElement>(null);
  const confirmedTableRef = useRef<HTMLDivElement>(null);
  const waitlistTableRef = useRef<HTMLDivElement>(null);
  const cancelledTableRef = useRef<HTMLDivElement>(null);
  const multiSelectBookingOptionsContainerRef = useRef<HTMLDivElement>(null);

  const [currentLocation, setCurrentLocation] = useState<CurrentLocation>(
    CurrentLocation.confirmed
  );

  useScrollPosition(
    ({prevPos, currPos}) => {
      // This is the height of the container (the contents scroll within this container)
      const containerClientHeight =
        scrollContainerRef.current?.clientHeight ?? 0;
      // This is our current scroll position
      let scrollPosition = scrollContainerRef.current?.scrollTop ?? 0;
      // This is the height of the scrollable contents (sum of all the inner tables)
      let totalHeight = 0;
      for (
        let childIndex = 0;
        childIndex < (scrollElementRef.current?.childElementCount ?? 0);
        childIndex++
      ) {
        totalHeight +=
          scrollElementRef.current?.children[childIndex].clientHeight ?? 0;
      }
      // Get the usable scroll height and calculate a percentage of where we are in the scroll
      let scrollHeight = totalHeight - containerClientHeight;
      let scrollPercent = scrollPosition / scrollHeight;
      // Loop through the child components and see which ones are in view.
      // NOTE: It will always favour the last component shown
      let currentLocation = 0;
      let containerTop = scrollContainerRef.current?.offsetTop ?? 0;
      for (
        let childIndex = 0;
        childIndex < (scrollElementRef.current?.childElementCount ?? 0);
        childIndex++
      ) {
        const element = scrollElementRef.current?.children[
          childIndex
        ]! as HTMLElement;
        // Get the client position as a percent of the total height.
        const clientPercent = (element.offsetTop - containerTop) / totalHeight;
        // If we have scrolled past this percentage then the client is in view
        if (clientPercent <= scrollPercent) {
          currentLocation = childIndex;
        }
      }
      setCurrentLocation(currentLocation as CurrentLocation);
    },
    scrollContainerRef,
    scrollElementRef,
    []
  );

  const scrollToTable = (tableRef: RefObject<HTMLDivElement>): void => {
    tableRef.current?.scrollIntoView();
  };

  const {
    domainStore: {eventsDomainStore},
    uiStore: {eventsUiStore, modalStore}
  } = useStores();

  useEffect(() => {
    if (!modalStore.isOpen && eventsUiStore.updateBookingError) {
      let body: string = "";
      let title: string = "";

      switch (eventsUiStore.updateBookingError) {
        case "createBookingNotEnoughCapacity":
          body = t("event:messages.createBookingFailedNotEnoughCapacity");
          title = t("event:titles.createBookingFailedNotEnoughCapacity");
          break;
        case "createBookingFailed":
          body = t("event:messages.createBookingFailed");
          title = t("event:titles.createBookingFailed");
          break;
        case "deleteBookingFailed":
          body = t("event:messages.deleteBookingFailed");
          title = t("event:titles.deleteBookingFailed");
          break;
        case "editBookingFailed":
          body = t("event:messages.editBookingFailed");
          title = t("event:titles.editBookingFailed");
          break;
        case "createBookingAlreadyExists":
        case "createBookingConfilictingRequest":
          body = t("event:messages.conflictingRequest");
          title = t("event:titles.editBookingFailed");
          break;
      }

      modalStore.openModalWithContent({
        content: body,
        title: title,
        primaryButtonText: t("event:actions.confirmRefresh"),
        centered: true,
        canToggle: false,
        onPrimaryClick: () => {
          modalStore.toggle();
          // clear the error straight away, to prevent this modal from reshowing
          eventsUiStore.updateBookingError = undefined;

          eventsUiStore.reloadEventDetailsAndBookings();
          eventsUiStore.clearSelectedBookings();
        },
        hideFooterBackground: true,
        footerButtonAlignment: "start"
      });
    }
  }, [
    t,
    eventsUiStore,
    modalStore,
    modalStore.isOpen,
    modalStore.toggle,
    eventsUiStore.updateBookingError
  ]);

  const eventDetails = eventsDomainStore.editableEvent;
  const waitlistEventBookings = eventsUiStore.waitlistEventBookings;
  const cancelledEventBookings = eventsUiStore.cancelledEventBookings;
  const totalRefunds = eventsUiStore.confirmedEventBookings.filter(
    (b) => b.isPaid
  ).length;

  const shouldShowRefundBanner =
    totalRefunds > 0 &&
    EventDetailsHelper.isCancelledOrPostponedEvent(eventDetails);

  const confirmedBookings = eventsUiStore.confirmedEventBookings.length;
  const totalCapacity =
    eventDetails?.bookingSpacesDetails.totalSpace.number ?? 0;
  const confirmedPillText =
    totalCapacity > 0
      ? `${confirmedBookings}/${totalCapacity}`
      : `${confirmedBookings}`;

  let multiSelectBookingOptionsContainerHeight = 0;
  const handleMultiSelectBookingOptionsRendered = (): void => {
    const scrollContainer = scrollContainerRef.current;
    const containerMultiSelectBookingOptions =
      multiSelectBookingOptionsContainerRef.current;

    if (scrollContainer && containerMultiSelectBookingOptions) {
      const containerClientHeight = scrollContainer.clientHeight;
      multiSelectBookingOptionsContainerHeight =
        containerMultiSelectBookingOptions.clientHeight;
      scrollContainerRef.current.style.height = `${
        containerClientHeight - multiSelectBookingOptionsContainerHeight
      }px`;
    }
  };

  const handleMultiSelectBookingOptionsHidden = (): void => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const containerClientHeight = scrollContainer.clientHeight;
      scrollContainerRef.current.style.height = `${
        containerClientHeight + multiSelectBookingOptionsContainerHeight
      }px`;
    }
  };

  const searchTermText = useMemo(
    () =>
      new TextObservable(
        "", // search term
        null,
        undefined,
        () => {
          searchTermText.text = searchTermText.text.trimLeft();
        } // onChange
      ),
    []
  );

  const reloadPageAfterDownloadError = (): void => {
    modalStore.openModalWithContent({
      content: t("event:errors.download.content"),
      title: t("event:errors.download.title"),
      primaryButtonText: t("event:actions.confirmRefresh"),
      centered: true,
      canToggle: false,
      onPrimaryClick: () => {
        modalStore.toggle();
        eventsUiStore.reloadEventDetailsAndBookings();
        eventsUiStore.clearSelectedBookings();
      },
      hideFooterBackground: true,
      footerButtonAlignment: "start"
    });
  };

  const csvColumns = [
    {
      id: "index",
      displayName: t("event:csv.columnNumber")
    },
    {
      id: "source",
      displayName: t("event:csv.columnSource")
    },
    {
      id: "name",
      displayName: t("event:csv.columnResidentName")
    },
    {
      id: "phoneNumber",
      displayName: t("event:csv.columnPhoneNumber")
    },
    {
      id: "unitCode",
      displayName: t("event:csv.columnUnitCode")
    },
    {
      id: "area",
      displayName: t("event:csv.columnArea")
    },
    {
      id: "residentNotes",
      displayName: t("event:csv.columnResidentNotes")
    },
    {
      id: "staffNotes",
      displayName: t("event:csv.columnStaffNotes")
    },
    {
      id: "status",
      displayName: t("event:csv.columnStatus")
    },
    ...(eventDetails?.priceDetails?.paidType.text === EventPaidType.fixed
      ? [
          {
            id: "paid",
            displayName: t("event:csv.columnPaid")
          }
        ]
      : [])
  ];

  const csvFileName =
    eventsDomainStore.getVillageCode() +
    "_" +
    (eventDetails.eventDateDetails.startDate.date
      ? format(eventDetails.eventDateDetails.startDate.date!, "ddMMMM")
      : "") +
    "_" +
    eventDetails?.title?.text.replaceAll(" ", "") +
    "_" +
    format(new Date(), "yyyyMMdd");

  const loadResidentData = async () => {
    let bookingsDisplayed = eventsUiStore.allBookings;

    if (searchTermText.text) {
      const loweredSearchTerm = searchTermText.text.toLowerCase();

      const isMatch = (value: string | null | undefined): boolean =>
        (value ?? "").toLowerCase().includes(loweredSearchTerm);

      bookingsDisplayed = eventsUiStore.allBookings.filter(
        (x) =>
          isMatch(x.residentData.name) ||
          isMatch(x.residentData.unitCode) ||
          isMatch(x.residentData.phoneNumber) ||
          isMatch(x.notes.staffNote) ||
          isMatch(x.notes.residentNote)
      );
    }

    const csvData = bookingsDisplayed.map((b) => {
      return {
        index: b.index.toString(),
        source: b.source,
        name: b.residentData.name?.replaceAll('"', '""'),
        phoneNumber: b.residentData.phoneNumber,
        unitCode: b.residentData.unitCode,
        area: residentCsvExporterConfigs.mapPersonTypeToArea(
          b.residentData.personTypeId
        ),
        residentNotes: b.notes.residentNote
          ? b.notes.residentNote.replaceAll('"', '""')
          : "",
        staffNotes: b.notes.staffNote
          ? b.notes.staffNote.replaceAll('"', '""')
          : "",
        status: b.status,
        paid: b.isPaidByFee ? "Account" : b.isPaid ? "Yes" : "No"
      };
    });

    return csvData;
  };

  return (
    <div className="tab-header has-fixed-scroll-area">
      <div>
        {/* Confirmed link */}
        <div
          className={classNames({
            "is-inactive": searchTermText.text
          })}
          onClick={() => scrollToTable(confirmedTableRef)}
        >
          <div
            className={classNames({
              "is-active": currentLocation === CurrentLocation.confirmed
            })}
          >
            {t("event:labels.bookingStatus.confirmed")}
          </div>
          <div data-testid="confirmedCount">
            <Tag
              text={confirmedPillText}
              type={
                currentLocation === CurrentLocation.confirmed &&
                !eventDetails.isPassedEndDate
                  ? "success"
                  : "light"
              }
            />
          </div>
        </div>

        {/* Waitlist link */}
        {!!waitlistEventBookings.length && (
          <div
            className={classNames({
              "is-inactive": searchTermText.text
            })}
            onClick={() => scrollToTable(waitlistTableRef)}
          >
            <div
              className={classNames({
                "is-active": currentLocation === CurrentLocation.waitlist
              })}
            >
              {t("event:labels.bookingStatus.waitlist")}
            </div>
            <div data-testid="waitlistCount">
              <Tag
                text={waitlistEventBookings.length.toString()}
                type={
                  currentLocation === CurrentLocation.waitlist &&
                  !eventDetails.isPassedEndDate
                    ? "warning"
                    : "light"
                }
              />
            </div>
          </div>
        )}

        {/* Cancelled link */}
        {cancelledEventBookings && !!cancelledEventBookings.length && (
          <div
            className={classNames({
              "is-inactive": searchTermText.text
            })}
            onClick={() => scrollToTable(cancelledTableRef)}
          >
            <div
              className={classNames({
                "is-active": currentLocation === CurrentLocation.cancelled
              })}
            >
              {t("event:labels.bookingStatus.cancelled")}
            </div>
            <div data-testid={"cancelledCount"}>
              <Tag
                text={cancelledEventBookings.length.toString()}
                type={
                  currentLocation === CurrentLocation.cancelled &&
                  !eventDetails.isPassedEndDate
                    ? "negative"
                    : "light"
                }
              />
            </div>
          </div>
        )}

        <div className="bookings-search-box">
          <SearchBox
            value={searchTermText}
            type={SearchBoxType.Live}
            placeholder={t("residentadmin:labels.searchPlaceholder")}
            onClear={() => {}}
          />
        </div>

        <div className="bookings-export-button">
          <CsvDownloader
            filename={csvFileName}
            extension=".csv"
            columns={csvColumns}
            datas={() => loadResidentData()}
            wrapColumnChar='"'
            handleError={() => reloadPageAfterDownloadError()}
          >
            <ButtonIcon type="secondary" icon={IconSource.export}>
              {t("eventTemplate:labels.export")}
            </ButtonIcon>
          </CsvDownloader>
        </div>
      </div>

      {!EventDetailsHelper.isCancelledOrPostponedEvent(eventDetails) &&
        eventsUiStore.selectedBookingIds.size > 0 && (
          <div ref={multiSelectBookingOptionsContainerRef!}>
            <MultiSelectBookingOptions
              onRender={handleMultiSelectBookingOptionsRendered}
              onUnmount={handleMultiSelectBookingOptionsHidden}
            />
          </div>
        )}

      {shouldShowRefundBanner && (
        <div
          className="refund-banner-wrapper"
          data-testid="eventBookingRefundsWarning"
        >
          <Banner
            text={t("event:messages.cancelledPostponedEventBanner", {
              count: totalRefunds
            })}
            icon={IconSource.error}
            enableMarkup={true}
          />
        </div>
      )}

      <div ref={scrollContainerRef!}>
        <div ref={scrollElementRef!} className="booking-tables">
          {!searchTermText.text && (
            <>
              <div ref={confirmedTableRef}>
                <EventBookingsConfirmedTable />
              </div>
              <div ref={waitlistTableRef}>
                <EventBookingsWaitlistTable />
              </div>
              {cancelledEventBookings && (
                <div ref={cancelledTableRef}>
                  <EventBookingsCancelledTable />
                </div>
              )}
            </>
          )}
          {searchTermText.text && (
            <EventBookingsSearchTable searchTerm={searchTermText} />
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(ManageBookingsTemplate);
